<template>
  <p v-if="apiLoading && !prescriptionsFormFieldItems.length">Loading your prescriptions...</p>

  <mpf-form
    v-if="!apiLoading || prescriptionsFormFieldItems.length"
    :fields="form?.fields"
    @submit="(e) => handleSubmit(e)"
    submit-label="Continue"
    :form-errors="prescriptionsFormErrors"
    v-bind="formComputedProps"
  >
    <template #error-content>
      <p>
        If you think this is a mistake, please call {{ tenant.localOrganisation.name }} on
        <a href="tel:{{tenant.localOrganisation.phone}}">{{ tenant.localOrganisation.phone }}</a
        >.
      </p>
    </template>
  </mpf-form>
</template>
<script setup lang="ts">
  import { useRepeatPrescriptionRequestStore } from '../../stores/useRepeatPrescriptionRequestStore';
  import { storeToRefs } from 'pinia';
  import { computed, reactive, ref, watch } from 'vue';
  import MpfForm from '../components/MpfForm/MpfForm.vue';

  import type { MpfCheckboxItem } from '../components/MpfCheckboxes/MpfCheckboxes.props';
  import type {
    MpfCheckboxesFieldConfig,
    MpfFormFieldConfig,
  } from '../components/MpfForm/MpfForm.props';
  import { router } from '../../router';
  import { inputAvailablePrescriptions, inputAvailableAcutePrescriptions } from '../../data/inputs';
  import { PrescriptionMedication } from '../../types/repeat-prescription-request';
  import { useTenantStore } from '../../stores/useTenantStore';
  import { MpfFormProps } from '../components/MpfForm/MpfForm.props';
  import { useRequestFormAvailability } from '../composables/useRequestFormAvailability';
  import { RequestType } from '../../types';

  const tenantStore = useTenantStore();
  const { check } = useRequestFormAvailability();

  const { tenant } = storeToRefs(tenantStore);

  function buildHint(item: PrescriptionMedication): string {
    const parts = [
      item.quantity && `Quantity: ${item.quantity}`,
      item.dosageInstruction && `Dosage instructions: ${item.dosageInstruction}`,
      item.lastRequestedDateTime && `Last requested date: ${item.lastRequestedDateTime}`,
    ];

    return parts.filter(Boolean).join('\n');
  }

  const repeatPrescriptionRequestStore = useRepeatPrescriptionRequestStore();
  const { prescriptionsList, apiLoading, prescriptionsAcuteList } = storeToRefs(
    repeatPrescriptionRequestStore,
  );

  const prescriptionsFormErrors = ref(null);

  // fetch patient prescriptions
  repeatPrescriptionRequestStore.fetchPrescriptions();

  const prescriptionsFormFieldItems = computed<MpfCheckboxItem[]>(() =>
    prescriptionsList.value.map((item) => ({
      label: `${item.productName} `,
      hint: buildHint(item),
      value: item.id,
      type: 'checkbox',
      disabled: !item.canBeRequestedByPatient,
      hintSecond: !item.canBeRequestedByPatient
        ? item.nominatedDispenserName
          ? `Please contact ${item.nominatedDispenserName} to collect your prescription`
          : 'Please contact your usual pharmacy to collect your prescription'
        : null,
    })),
  );

  const prescriptionsAcuteFormFieldItems = computed<MpfCheckboxItem[]>(() =>
    prescriptionsAcuteList.value.map((item) => ({
      label: `${item.productName} `,
      hint: buildHint(item),
      value: item.id,
      disabled: !item.canBeRequestedByPatient,
      hintSecond: !item.canBeRequestedByPatient
        ? item.nominatedDispenserName
          ? `Please contact ${item.nominatedDispenserName} to collect your prescription`
          : 'Please contact your usual pharmacy to collect your prescription'
        : null,
    })),
  );

  const validRepeatPrescriptionsCount = computed<number>(
    () =>
      prescriptionsFormFieldItems.value.filter((item: MpfCheckboxItem) => !item.disabled).length,
  );

  const validAcutePrescriptionsCount = computed<number>(
    () =>
      prescriptionsAcuteFormFieldItems.value.filter((item: MpfCheckboxItem) => !item.disabled)
        .length,
  );

  const validTotalPrescriptionsCount = computed<number>(
    () => validRepeatPrescriptionsCount.value + validAcutePrescriptionsCount.value,
  );

  const formComputedProps = reactive<
    Pick<MpfFormProps, 'errorSummaryTitle' | 'errorSummaryByLine' | 'showErrors' | 'submitLabel'>
  >({});

  watch(apiLoading, async (nv) => {
    if (!nv && validTotalPrescriptionsCount.value === 0) {
      formComputedProps.showErrors = true;
      formComputedProps.errorSummaryTitle = 'No prescriptions available';
      formComputedProps.errorSummaryByLine =
        'You do not have any prescriptions that can be requested online.';
      formComputedProps.submitLabel = 'Start again';
    }
  });

  // if all disabled change label & hint

  const formInput = computed<MpfCheckboxesFieldConfig>(() => ({
    ...inputAvailablePrescriptions,
    ...(validTotalPrescriptionsCount.value === 0 ? { hint: '' } : {}),
    items: prescriptionsFormFieldItems.value,
    initialValue: repeatPrescriptionRequestStore.selectedPrescriptionsList,
  }));

  // TODO: do we need 2 inputs?
  const formInputAcute = computed<MpfCheckboxesFieldConfig>(() => ({
    ...inputAvailableAcutePrescriptions,
    ...(validTotalPrescriptionsCount.value === 0 ? { hint: '' } : {}),
    items: prescriptionsAcuteFormFieldItems.value,
    initialValue: repeatPrescriptionRequestStore.selectedPrescriptionsAcuteList,
  }));

  const form = computed<{
    fields: MpfFormFieldConfig[];
  }>(() => ({
    fields: [
      ...(validRepeatPrescriptionsCount.value ? [formInput.value] : []),
      ...(validAcutePrescriptionsCount.value ? [formInputAcute.value] : []),
    ],
  }));

  const handleSubmit = async (data: any) => {
    if (validTotalPrescriptionsCount.value === 0) {
      // if none selectable start again
      await router.push('/');
    }

    if (!data.valid) return;

    const formStatus = await check('repeat-prescription-request' as RequestType);
    if (!formStatus) return;

    // Keeping selected items separate here but merging in the store for submission
    const items = data.values.availablePrescriptions;
    const itemsAcute = data.values.availablePrescriptionsAcute;
    if (!items?.length && !itemsAcute?.length) {
      prescriptionsFormErrors.value = [
        ...(validRepeatPrescriptionsCount.value > 0
          ? [
              {
                name: 'availablePrescriptions',
                errors: ['Please select one or more items from the list'],
              },
            ]
          : []),
        ...(validAcutePrescriptionsCount.value > 0
          ? [
              {
                name: 'availablePrescriptionsAcute',
                errors: validRepeatPrescriptionsCount.value
                  ? []
                  : ['Please select one or more items from the list'],
              },
            ]
          : []),
      ];
      return;
    }

    prescriptionsFormErrors.value = [];
    repeatPrescriptionRequestStore.setSelectedPrescriptionsList(items);
    repeatPrescriptionRequestStore.setSelectedPrescriptionsAcuteList(itemsAcute);

    await router.push('/repeat-prescription-request/summary');
  };
</script>
